<template>
  <div style="max-width: 75vw">
    <v-form>
      <div class="mb-8">
        <h2 class="page-label mb-2">1. Medicamentos solicitados</h2>

        <v-card
          v-for="medicine in lmeData.medicines"
          :key="medicine.id"
          class="px-4 pb-4 mb-4 card-border"
          flat
          color="#F9FAFE"
        >
          <v-card-title>
            <span class="card-title">{{ medicine.name }}</span>
          </v-card-title>
          <v-card-text>
            <p class="card-text">Informe a quantidade para cada mês</p>
            <v-row dense>
              <v-col v-for="n in 6" :key="n">
                <v-text-field
                  :label="n + '° mês'"
                  dense
                  v-mask="['####']"
                  v-model="medicine[`month${n}`]"
                  outlined
                  hide-details
                  :min="0"
                  :height="48"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </div>
      <div class="mb-8">
        <h2 class="page-label mb-2">2. Informações</h2>
        <div>
          <v-text-field
            label="Medicamentos anteriores"
            dense
            outlined
            hide-details
            :height="48"
            class="mb-6"
            v-model="lmeData.previousTreatments"
          />
          <v-autocomplete
            label="CID"
            dense
            outlined
            hide-details
            :height="48"
            menu-props="closeOnContentClick"
            class="mb-6"
            v-model="lmeData.cid"
            @update:search-input="fetchCID"
            :items="cidAutocomplete.options"
            :search-input="cidAutocomplete.search"
          />

          <v-select
            label="Local"
            outlined
            dense
            :height="48"
            attach="#local_selec"
            item-text="name"
            item-value="id"
            no-data-text="Nenhum dado encontrado"
            :items="attendance.physicianCnes"
            v-model="lmeData.location"
            class="mb-6"
            hide-details
          >
            <template #append-outer>
              <div id="local_selec"></div>
            </template>
          </v-select>
          <div class="d-flex align-center justify-space-between mb-6 gap-6">
            <v-text-field
              label="Nome da mãe"
              dense
              outlined
              hide-details
              v-model="lmeData.patientMotherName"
              :height="48"
            />
            <v-select
              label="Cor da pele"
              :items="skinColors"
              outlined
              hide-details
              dense
              :height="48"
              attach="#skinColor_selec"
              item-text="text"
              item-value="value"
              v-model="lmeData.patientSkinColor"
            >
              <template #append-outer>
                <div id="skinColor_selec"></div>
              </template>
            </v-select>
          </div>

          <div class="d-flex align-start justify-space-between mb-6 gap-6">
            <div class="d-flex flex-column radio-attestation-width">
              <p class="field-title">Atestado de incapacidade?</p>

              <v-radio-group
                v-model="lmeData.attested"
                row
                mandatory
                @change="handleRadioGroup"
                hide-details
                class="mt-0"
              >
                <v-radio label="Sim" value="SIM"></v-radio>
                <v-radio label="Não" value="NAO" class="ml-8"></v-radio>
              </v-radio-group>
            </div>
            <v-text-field
              label="Responsável pelo atestado"
              dense
              outlined
              hide-details
              v-model="lmeData.responsibleName"
              :height="48"
              v-if="lmeData.attested === 'SIM'"
            />
          </div>
          <div class="d-flex flex-column mb-6 grid-3-columns">
            <p class="field-title">Imprimir com data atual?</p>
            <v-radio-group v-model="lmeData.printCurrentDate" row mandatory hide-details>
              <v-radio label="Sim" value="SIM"></v-radio>
              <v-radio label="Não" value="NAO" class="ml-8"></v-radio>
            </v-radio-group>
          </div>
        </div>
      </div>
      <div class="mb-4">
        <h2 class="page-label mb-4">3. Anamnese</h2>
        <div>
          <v-textarea
            label="Anamnese"
            outlined
            dense
            counter="5000"
            v-model="lmeData.anamnesis"
            :rows="1"
            auto-grow
            row-height="40"
          />
        </div>
      </div>
    </v-form>
    <div class="d-flex flex-row gap-4 justify-end mb-4">
      <download-frame :url="printEmptyLmeUrl" alwaysForceDownload>
        <v-btn text height="40" color="primary" class="new-btn"> Imprimir modelo vazio </v-btn>
      </download-frame>
      <download-frame :url="printLmeUrl" alwaysForceDownload remoteUrl :onBeforeDownload="saveLme">
        <v-btn text height="40" color="primary" class="new-btn rounded-lg" outlined> Imprimir LME </v-btn>
      </download-frame>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import { SKIN_COLOR } from '@/constants/person'
import { PUBLIC_STORAGE_URL } from '@/constants/global'
import DownloadFrame from '@/components/DownloadFrame.vue'
import { mask } from 'vue-the-mask'

const {
  mapMutations: newAttendanceFormMutations,
  mapGetters: newAttendanceFormGetters,
  mapActions: newAttendanceFormActions,
} = createNamespacedHelpers('newAttendanceForm')

export default {
  directives: {
    mask: {
      bind(el, binding, vnode, oldVnode) {
        if (binding.value) {
          mask(el, binding, vnode, oldVnode)
        }
      },
    },
  },
  components: { DownloadFrame },
  data() {
    return {
      active: true,
      publicStorageUrl: PUBLIC_STORAGE_URL,
      selectedTreatments: [],
      lmeData: {
        location: null,
        patientMotherName: null,
        patientSkinColor: null,
        attested: null,
        responsibleName: null,
        printCurrentDate: null,
        printSignature: null,
        anamnesis: null,
        medicines: [],
        cid: null,
        previousTreatments: null,
      },

      cidAutocomplete: {
        search: '',
        options: [],
      },
    }
  },
  computed: {
    .../** @type {{attendance: Computed<ObjectLiteral>, treatments: Computed<ObjectLiteral[]>}} */ (
      newAttendanceFormGetters(['attendance', 'treatments', 'extraAnswers', 'questions', 'answers'])
    ),
    /** @type {Computed<string>} */
    printEmptyLmeUrl() {
      return `${this.publicStorageUrl}/lme/lme-vazio.pdf`
    },
    /** @type {Computed<string>} */
    printLmeUrl() {
      return `${this.$util.apiURL()}/print-lme/${this.attendance.id}`
    },
    skinColors() {
      return [...SKIN_COLOR]
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(newVal) {
        this.selectedTreatments = this.$store.getters['newAttendanceForm/extraAnswers']?.selectedTreatments || []
        this.loadLme()
      },
    },
    lmeData: {
      deep: true,
      handler(newVal) {
        this.updateExtraAnswer({ name: 'lme-data', value: this.lmeData })
      },
    },
    selectedTreatments: {
      deep: true,
      handler(newVal) {
        this.lmeData.medicines = this.retrieveNewTreatments(newVal)
      },
    },
  },
  methods: {
    ...newAttendanceFormMutations(['updateExtraAnswer', 'updatePatientData']),
    ...newAttendanceFormActions(['saveAllAnswers']),

    loadLme() {
      this.lmeData.patientMotherName = this.extraAnswers['lme-data']?.patientMotherName
      this.lmeData.location = this.extraAnswers['lme-data']?.location
      this.lmeData.patientSkinColor = this.extraAnswers['lme-data']?.patientSkinColor
      this.lmeData.attested = this.extraAnswers['lme-data']?.attested
      this.lmeData.responsibleName = this.extraAnswers['lme-data']?.responsibleName
      this.lmeData.printCurrentDate = this.extraAnswers['lme-data']?.printCurrentDate
      this.lmeData.printSignature = this.extraAnswers['lme-data']?.printSignature
      this.lmeData.anamnesis = this.extraAnswers['lme-data']?.anamnesis
      this.lmeData.cid = this.extraAnswers['lme-data']?.cid
      this.lmeData.previousTreatments = this.extraAnswers['lme-data']?.previousTreatments
      this.lmeData.medicines = this.retrieveNewTreatments(this.selectedTreatments)
    },

    retrieveNewTreatments(selectedTreatments) {
      const extraAnswerMedicines = (this.extraAnswers['lme-data']?.medicines || []).filter(
        (medicine) => medicine.treatmentId,
      )

      return selectedTreatments.map((treatement) => {
        const alreadySaved = extraAnswerMedicines.find((medicine) => medicine.treatmentId === treatement.id)

        if (alreadySaved) {
          return alreadySaved
        }

        return {
          treatmentId: treatement.id,
          ...treatement.medicine,
          month1: undefined,
          month2: undefined,
          month3: undefined,
          month4: undefined,
          month5: undefined,
          month6: undefined,
        }
      })

      return this.lmeData.medicines
    },
    async saveLme() {
      await this.saveAllAnswers()
    },

    handleRadioGroup(val) {
      if (val === 'NAO') {
        this.lmeData.responsibleName = null
      }
    },

    async fetchCID(search) {
      this.cidAutocomplete.search = search || this.lmeData.cid || ''
      if (this.cidAutocomplete.search.length < 2) {
        return
      }
      try {
        this.cidAutocomplete.options = (await this.$api.fetchOptions('cid', this.cidAutocomplete.search))?.map(
          (cid) => cid.code,
        )
      } catch (error) {
        this.$root.error(error)
      }
    },
  },
}
</script>

<style scoped>
.field-title {
  color: var(--v-onSurface-base);
  font-weight: 400;
  font-size: 14px;
  letter-spacing: 0.25px;
  margin-bottom: 8px;
  line-height: 20px;
}

.page-label {
  color: var(--v-onSurface-base);
  font-weight: 500;
  font-size: 16px;
  letter-spacing: 0.15px;
  line-height: 24px;
}

.card-border {
  border-radius: 8px;
  border: 1px solid #dbd8de !important;
}
.card-title {
  font-weight: 400;
  font-size: 16px;
  letter-spacing: 0.5px;
  line-height: 24px;
  color: var(--v-onSurface-base);
}
.card-text {
  color: var(--v-onSurface-base);
  font-weight: 400;
  font-size: 14px;
  letter-spacing: 0.25px;
  line-height: 20px;
}

.radio-attestation-width {
  flex-grow: 1;
  max-width: 500px;
}
.v-input--radio-group :deep(.v-input--radio-group__input .v-radio label) {
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: var(--v-neutral-lighten17);
  height: 48px;
}
</style>
